/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import type { PenaltyPoint } from '@sportninja/common/features/playerFinder/penalties'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import React from 'react'
import { font, paragraphSmall } from 'src/components/css'
import PenaltyLineChart from './PenaltyLineChart'
import {
  detailSectionPanelStyle,
  detailSectionStyle,
  detailSectionTitleStyle,
} from './detailSectionStyles'

export interface PenaltySectionProps {
  /** Per-month history for the chart. Omit it to show just the total. */
  penalties?: PenaltyPoint[]
  /** Total penalty minutes (PiM); appended to the title as "(N min)". */
  penaltyMinutes?: number
}

/**
 * "Penalty Minutes" section: the season total in the title, over an optional
 * per-month penalty chart. The chart data is MOCK until the BE ships penalty
 * history (common/features/playerFinder/penalties — USE_MOCK_PENALTIES), so the
 * callers pass only the total for now; the total is real (derived from season
 * stats via usePenaltyMinutes).
 */
const PenaltySection: React.FC<PenaltySectionProps> = ({
  penalties,
  penaltyMinutes,
}) => {
  const chartData = penalties && penalties.length > 0 ? penalties : null
  if (!chartData && penaltyMinutes == null) {
    return null
  }
  const title =
    penaltyMinutes != null
      ? `${capitalizeWords('Penalty Minutes')} (${penaltyMinutes} min)`
      : capitalizeWords('Penalty Minutes')

  return (
    <div css={detailSectionStyle}>
      <span css={detailSectionTitleStyle}>{title}</span>
      {chartData && (
        <div css={[detailSectionPanelStyle, cardStyle]}>
          {chartData.some((p) => p.penalties > 0) ? (
            <PenaltyLineChart data={chartData} />
          ) : (
            <span css={emptyStyle}>No penalties</span>
          )}
        </div>
      )}
    </div>
  )
}

export default PenaltySection

const cardStyle = css`
  padding: 8px;
`

const emptyStyle = css`
  ${font.body}
  ${paragraphSmall}
  display: block;
  padding: 16px;
  text-align: center;
  color: ${colors.NEUTRAL_50};
`
